"use client";

import { useState, useEffect } from "react";
import Icon from "@/components/ui/AppIcon";

export interface FilterState {
  query: string;
  area: string;
  propertyType: string;
  transactionType: string;
  sortBy: string;
}

interface SearchFiltersProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  totalResults: number;
}

const AREAS = [
  "All Areas",
  "Port of Spain",
  "San Fernando",
  "Chaguanas",
  "Westmoorings",
  "Maraval",
  "Diego Martin",
  "Arima",
  "Couva",
  "Point Fortin",
  "Tobago",
  "Penal",
  "Siparia",
  "Sangre Grande",
  "Princes Town",
];

const PROPERTY_TYPES = [
  "All Types",
  "House",
  "Apartment",
  "Townhouse",
  "Land",
  "Commercial",
  "Other",
];

const TRANSACTION_TYPES = ["All", "Sale", "Rental"];

const SORT_OPTIONS = [
  { value: "recent", label: "Most Recent" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "price_asc", label: "Price: Low to High" },
];

export default function SearchFilters({
  filters,
  onChange,
  totalResults,
}: SearchFiltersProps) {
  const [showFilters, setShowFilters] = useState(true);

  const [localQuery, setLocalQuery] = useState(filters.query);

  const update = (key: keyof FilterState, value: string) => {
    if (key === "query") {
      setLocalQuery(value);
      return;
    }
    onChange({ ...filters, [key]: value });
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      onChange({ ...filters, query: localQuery });
    }, 400); // 400ms debounce

    return () => clearTimeout(handler);
  }, [localQuery]);

  // Sync internal state if filters.query changes from outside (e.g. clearAll)
  useEffect(() => {
    setLocalQuery(filters.query);
  }, [filters.query]);

  const clearAll = () => {
    onChange({
      query: "",
      area: "All Areas",
      propertyType: "All Types",
      transactionType: "All",
      sortBy: "recent",
    });
  };

  const hasActiveFilters =
    filters.area !== "All Areas" ||
    filters.propertyType !== "All Types" ||
    filters.transactionType !== "All" ||
    filters.query !== "";

  return (
    <div className="bg-white border border-border rounded-lg shadow-card">
      {/* Search Row */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Icon
              name="MagnifyingGlassIcon"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by street address or area..."
              value={localQuery}
              onChange={(e) => update("query", e.target.value)}
              className="field-input w-full pl-10 pr-4 py-2.5 border border-border rounded-md text-sm bg-sand placeholder-muted text-navy focus:bg-white transition-colors"
            />
            {localQuery && (
              <button
                onClick={() => update("query", "")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-navy"
              >
                <Icon name="XMarkIcon" size={16} />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-md border text-sm font-medium transition-all ${showFilters
              ? "bg-navy text-white border-navy" : "bg-white text-navy border-border hover:border-navy"
              }`}
          >
            <Icon name="AdjustmentsHorizontalIcon" size={16} />
            Filters
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-teal ml-0.5" />
            )}
          </button>

          <div className="hidden sm:flex items-center gap-2">
            <select
              value={filters.sortBy}
              onChange={(e) => update("sortBy", e.target.value)}
              className="field-input border border-border rounded-md px-3 py-2.5 text-sm text-navy bg-white cursor-pointer"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <div className="px-4 py-3 flex flex-wrap items-center gap-3 border-b border-border bg-sand/40">
          {/* Transaction Type Toggle */}
          <div className="flex items-center gap-1 bg-sand rounded-md p-0.5 border border-border">
            {TRANSACTION_TYPES.map((t) => (
              <button
                key={t}
                onClick={() => update("transactionType", t)}
                className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${filters.transactionType === t
                  ? "bg-white text-navy shadow-sm"
                  : "text-muted hover:text-navy"
                  }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Area Filter */}
          <select
            value={filters.area}
            onChange={(e) => update("area", e.target.value)}
            className="field-input border border-border rounded-md px-3 py-2 text-xs font-medium text-navy bg-white cursor-pointer"
          >
            {AREAS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>

          {/* Property Type Filter */}
          <select
            value={filters.propertyType}
            onChange={(e) => update("propertyType", e.target.value)}
            className="field-input border border-border rounded-md px-3 py-2 text-xs font-medium text-navy bg-white cursor-pointer"
          >
            {PROPERTY_TYPES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          {/* Results count + clear */}
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-muted font-body">
              <span className="font-semibold text-navy">{totalResults}</span>{" "}
              {totalResults === 1 ? "result" : "results"}
            </span>
            {hasActiveFilters && (
              <button
                onClick={clearAll}
                className="text-xs text-teal hover:text-teal-light font-medium flex items-center gap-1"
              >
                <Icon name="XMarkIcon" size={13} />
                Clear all
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}